import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Avatar, Chip, IconButton, Alert,
  CircularProgress, useTheme, useMediaQuery, Tooltip,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';
import CalendarioAsistencia from '../components/liquidacion/CalendarioAsistencia';

const TIPO_PAGO_CONFIG = {
  SUELDO: { label: 'Sueldo', color: '#2E7D32', bg: '#E8F5E9', icon: '💼' },
  JORNAL: { label: 'Jornal', color: '#E65100', bg: '#FFF3E0', icon: '🔨' },
};

const AsistenciaMasiva = () => {
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const moneda = simboloMoneda || 'S/';

  const [fecha, setFecha] = useState(new Date());
  const [trabajadores, setTrabajadores] = useState([]);
  const [asistencias, setAsistencias] = useState({}); // { id_trabajador: true/false }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // id_trabajador que está guardando
  const [error, setError] = useState('');
  const [calendarioOpen, setCalendarioOpen] = useState(false);
  const [calendarioTrabajador, setCalendarioTrabajador] = useState(null);

  const fechaStr = fecha.toISOString().split('T')[0];
  const esHoy = fechaStr === new Date().toISOString().split('T')[0];
  const esFuturo = fecha > new Date();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Obtener trabajadores SUELDO y JORNAL
      const trabRes = await axios.get(`${config.API_BASE_URL}/api/trabajadores`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const trabFiltrados = trabRes.data.filter(t =>
        t.tipo_pago === 'SUELDO' || t.tipo_pago === 'JORNAL'
      );

      // Para cada trabajador, verificar si tiene asistencia en esta fecha
      const asistMap = {};
      await Promise.all(trabFiltrados.map(async (t) => {
        try {
          const res = await axios.get(
            `${config.API_BASE_URL}/api/liquidacion/asistencia/${t.id_trabajador}?mes=${fecha.getMonth() + 1}&anio=${fecha.getFullYear()}`,
            { headers: { Authorization: `Bearer ${user.token}` } }
          );
          const tieneHoy = res.data.asistencias?.some(a =>
            new Date(a.fecha).toISOString().split('T')[0] === fechaStr
          );
          asistMap[t.id_trabajador] = {
            presente: tieneHoy,
            dias_mes: res.data.dias_trabajados || 0,
            total_mes: res.data.total_monto || 0,
          };
        } catch (e) {
          asistMap[t.id_trabajador] = { presente: false, dias_mes: 0, total_mes: 0 };
        }
      }));

      setTrabajadores(trabFiltrados);
      setAsistencias(asistMap);
    } catch (e) {
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [user.token, fechaStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleAsistencia = async (trabajador) => {
    if (esFuturo) return;
    const id = trabajador.id_trabajador;
    const actual = asistencias[id]?.presente;
    setSaving(id);

    try {
      if (actual) {
        // Quitar asistencia
        await axios.delete(
          `${config.API_BASE_URL}/api/liquidacion/asistencia/${id}/${fechaStr}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      } else {
        // Marcar asistencia
        await axios.post(
          `${config.API_BASE_URL}/api/liquidacion/asistencia`,
          { id_trabajador: id, fecha: fechaStr },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
      }
      setAsistencias(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          presente: !actual,
          dias_mes: actual ? (prev[id]?.dias_mes || 1) - 1 : (prev[id]?.dias_mes || 0) + 1,
        },
      }));
    } catch (e) {
      setError(e.response?.data?.error || 'Error al registrar');
    } finally {
      setSaving(null);
    }
  };

  const marcarTodos = async () => {
    if (esFuturo) return;
    const sinMarcar = trabajadores.filter(t => !asistencias[t.id_trabajador]?.presente);
    if (sinMarcar.length === 0) return;

    setSaving('all');
    try {
      await Promise.all(sinMarcar.map(t =>
        axios.post(
          `${config.API_BASE_URL}/api/liquidacion/asistencia`,
          { id_trabajador: t.id_trabajador, fecha: fechaStr },
          { headers: { Authorization: `Bearer ${user.token}` } }
        ).catch(() => {})
      ));
      await fetchData();
    } catch (e) {
      setError('Error al marcar todos');
    } finally {
      setSaving(null);
    }
  };

  const cambiarFecha = (dias) => {
    const nueva = new Date(fecha);
    nueva.setDate(nueva.getDate() + dias);
    setFecha(nueva);
  };

  const presentes = trabajadores.filter(t => asistencias[t.id_trabajador]?.presente).length;
  const total = trabajadores.length;
  const diaSemana = fecha.toLocaleDateString('es-PE', { weekday: 'long' });
  const fechaDisplay = fecha.toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/home/trabajadores')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, color: '#1A237E' }}>
            Asistencia del Equipo
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748B' }}>
            Marca quién trabajó hoy — sueldo fijo y jornal
          </Typography>
        </Box>
      </Box>

      {/* Navegación de fecha */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 2, mb: 3, py: 2, px: 3,
        bgcolor: esHoy ? '#E8F5E9' : '#F8FAFC',
        borderRadius: 3, border: '1px solid', borderColor: esHoy ? '#A5D6A7' : '#E2E8F0',
      }}>
        <IconButton onClick={() => cambiarFecha(-1)} size="small"><PrevIcon /></IconButton>
        <Box sx={{ textAlign: 'center', flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#1E293B', textTransform: 'capitalize' }}>
            {diaSemana}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748B' }}>
            {fechaDisplay}
          </Typography>
          {esHoy && (
            <Chip label="HOY" size="small" sx={{ mt: 0.5, bgcolor: '#2E7D32', color: '#fff', fontWeight: 700, fontSize: 10 }} />
          )}
          {esFuturo && (
            <Chip label="FUTURO" size="small" sx={{ mt: 0.5, bgcolor: '#94A3B8', color: '#fff', fontWeight: 700, fontSize: 10 }} />
          )}
        </Box>
        <IconButton onClick={() => cambiarFecha(1)} size="small"><NextIcon /></IconButton>
      </Box>

      {/* Resumen */}
      <Box sx={{
        display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap',
      }}>
        <Box sx={{
          flex: 1, minWidth: 100, p: 1.5, borderRadius: 2.5,
          bgcolor: '#E8F5E9', border: '1px solid #A5D6A7', textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#2E7D32' }}>{presentes}</Typography>
          <Typography sx={{ fontSize: 11, color: '#2E7D32', fontWeight: 600 }}>Presentes</Typography>
        </Box>
        <Box sx={{
          flex: 1, minWidth: 100, p: 1.5, borderRadius: 2.5,
          bgcolor: '#FFEBEE', border: '1px solid #EF9A9A', textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#C62828' }}>{total - presentes}</Typography>
          <Typography sx={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>Ausentes</Typography>
        </Box>
        <Box sx={{
          flex: 1, minWidth: 100, p: 1.5, borderRadius: 2.5,
          bgcolor: '#E3F2FD', border: '1px solid #90CAF9', textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#1565C0' }}>{total}</Typography>
          <Typography sx={{ fontSize: 11, color: '#1565C0', fontWeight: 600 }}>Total</Typography>
        </Box>
      </Box>

      {/* Botón marcar todos */}
      {!esFuturo && presentes < total && (
        <Box
          onClick={saving ? undefined : marcarTodos}
          sx={{
            mb: 2, p: 1.5, borderRadius: 2.5, textAlign: 'center',
            border: '2px dashed #A5D6A7', cursor: 'pointer', color: '#2E7D32',
            '&:hover': { bgcolor: '#E8F5E9', borderColor: '#2E7D32' },
            transition: 'all 0.2s',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            {saving === 'all' ? 'Marcando...' : `Marcar todos como presentes (${total - presentes} faltantes)`}
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Lista de trabajadores */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : trabajadores.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#94A3B8', fontSize: 14 }}>
            No hay trabajadores con tipo Sueldo o Jornal
          </Typography>
          <Typography sx={{ color: '#CBD5E1', fontSize: 12, mt: 1 }}>
            Configura el tipo de pago en el formulario de cada trabajador
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {trabajadores.map(t => {
            const info = asistencias[t.id_trabajador] || {};
            const tipoCfg = TIPO_PAGO_CONFIG[t.tipo_pago] || TIPO_PAGO_CONFIG.JORNAL;
            const presente = info.presente;
            const isSaving = saving === t.id_trabajador;
            const initials = `${(t.nombre_trabajador || '')[0]}${(t.apellido_trabajador || '')[0]}`;

            return (
              <Box key={t.id_trabajador} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1.5, borderRadius: 3,
                border: '1px solid',
                borderColor: presente ? '#A5D6A7' : '#E2E8F0',
                bgcolor: presente ? '#F1F8E9' : '#fff',
                transition: 'all 0.2s',
                cursor: esFuturo ? 'default' : 'pointer',
                opacity: esFuturo ? 0.5 : 1,
                '&:hover': esFuturo ? {} : { borderColor: presente ? '#EF9A9A' : '#A5D6A7', transform: 'translateX(4px)' },
              }}
                onClick={() => !esFuturo && !isSaving && toggleAsistencia(t)}
              >
                {/* Avatar */}
                <Avatar sx={{
                  width: 40, height: 40, fontSize: 14, fontWeight: 800,
                  bgcolor: presente ? '#2E7D32' : '#E0E0E0',
                  color: presente ? '#fff' : '#9E9E9E',
                  transition: 'all 0.2s',
                }}>
                  {isSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> :
                    presente ? <CheckIcon sx={{ fontSize: 20 }} /> : initials}
                </Avatar>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E293B' }} noWrap>
                    {t.nombre_trabajador} {t.apellido_trabajador}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={tipoCfg.label} size="small" sx={{
                      height: 18, fontSize: 10, fontWeight: 700,
                      bgcolor: tipoCfg.bg, color: tipoCfg.color,
                    }} />
                    <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                      {t.tipo_pago === 'JORNAL'
                        ? `${moneda} ${parseFloat(t.monto_jornal || 0).toFixed(2)}/día`
                        : `${moneda} ${parseFloat(t.monto_sueldo || 0).toFixed(2)}/mes`
                      }
                    </Typography>
                  </Box>
                </Box>

                {/* Días del mes */}
                <Box sx={{ textAlign: 'center', minWidth: 50 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1E293B' }}>
                    {info.dias_mes || 0}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: '#94A3B8' }}>días/mes</Typography>
                </Box>

                {/* Botón calendario */}
                <Tooltip title="Ver calendario">
                  <IconButton size="small" onClick={(e) => {
                    e.stopPropagation();
                    setCalendarioTrabajador(t);
                    setCalendarioOpen(true);
                  }} sx={{ color: tipoCfg.color, width: 32, height: 32 }}>
                    <CalendarIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Calendario individual */}
      <CalendarioAsistencia
        open={calendarioOpen}
        onClose={() => setCalendarioOpen(false)}
        trabajador={calendarioTrabajador}
        onUpdate={fetchData}
      />
    </Box>
  );
};

export default AsistenciaMasiva;
